import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const TaskForm = ({ visible, onCancel, onSuccess, task, clients, users, groups }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client: '',
    assignedTo: [],
    assignedGroups: [],
    deadline: '',
    status: 'pending',
    verificationTypes: [] // Added verification types
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    title: '',
    type: '',
    message: ''
  });
  const [verificationTypes, setVerificationTypes] = useState([]); // Added state for verification types

  useEffect(() => {
    if (task) {
      setFormData({
        name: task.name,
        description: task.description,
        client: task.client?._id || '',
        assignedTo: task.assignedTo.map(user => user._id),
        assignedGroups: task.assignedGroups?.map(group => group._id) || [],
        deadline: new Date(task.deadline).toISOString().split('T')[0],
        status: task.status,
        verificationTypes: task.verificationTypes || [] // Added verification types
      });
      setSearchTerm(task.client?.clientName || '');
    }
    fetchVerificationTypes(); // Fetch verification types on mount
  }, [task]);

  const fetchVerificationTypes = async () => {
    try {
      const response = await api.get('admin/verification-types');
      setVerificationTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch verification types:', error);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      const filtered = clients.filter(client =>
        client.clientName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredClients([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, clients]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const displayToast = (title, type, message = '') => {
    setToastMessage({ title, type, message });
    setShowToast(true);
  };

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVerificationTypeChange = (e) => {
    const selectedTypes = Array.from(e.target.selectedOptions, option => option.value);
    setFormData(prev => ({
      ...prev,
      verificationTypes: selectedTypes.map(id => ({ id }))
    }));
  };

  const handleClientSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClientSelect = (client) => {
    setFormData(prev => ({
      ...prev,
      client: client._id
    }));
    setSearchTerm(client.clientName);
    setShowSuggestions(false);
  };

  const handleUserSelect = (userId) => {
    if (userId && !formData.assignedTo.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        assignedTo: [...prev.assignedTo, userId]
      }));
    }
  };

  const handleGroupSelect = (groupId) => {
    if (groupId && !formData.assignedGroups.includes(groupId)) {
      setFormData(prev => ({
        ...prev,
        assignedGroups: [...prev.assignedGroups, groupId]
      }));
    }
  };

  const removeGroup = (groupId) => {
    setFormData(prev => ({
      ...prev,
      assignedGroups: prev.assignedGroups.filter(id => id !== groupId)
    }));
  };

  const removeUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assignedTo: prev.assignedTo.filter(id => id !== userId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (task) {
        await api.put(`/tasks/${task._id}`, formData);
        displayToast('Task updated', 'success');
      } else {
        await api.post('/tasks', formData);
        displayToast('Task created', 'success');
      }
      onSuccess();
    } catch (error) {
      displayToast('Error', 'error', error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedUsers = () => {
    return formData.assignedTo.map(userId => {
      const user = users.find(u => u._id === userId);
      return user ? { id: userId, name: `${user.firstName} ${user.lastName}` } : null;
    }).filter(Boolean);
  };

  const getSelectedGroups = () => {
    return formData.assignedGroups.map(groupId => {
      const group = groups.find(g => g._id === groupId);
      return group ? { id: groupId, name: group.name } : null;
    }).filter(Boolean);
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center overflow-y-auto ${visible ? '' : 'hidden'}`}>
      {showToast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg shadow-lg ${
          toastMessage.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          <div className="font-semibold">{toastMessage.title}</div>
          {toastMessage.message && <div className="text-sm mt-1">{toastMessage.message}</div>}
        </div>
      )}
      <div className="fixed inset-0 bg-black opacity-50"></div>
      <div className="relative bg-white rounded-lg w-full max-w-4xl my-8 mx-4">
        <form onSubmit={handleSubmit} className="w-full">
          <div className="px-6 py-4 border-b">
            <h2 className="text-xl font-semibold">{task ? 'Edit Task' : 'Create Task'}</h2>
            <button 
              onClick={onCancel}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="px-6 py-4 space-y-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter task name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter task description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="4"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client
                </label>
                <input
                  value={searchTerm}
                  onChange={handleClientSearch}
                  placeholder="Search client..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {showSuggestions && filteredClients.length > 0 && (
                  <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
                    {filteredClients.map(client => (
                      <div
                        key={client._id}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleClientSelect(client)}
                      >
                        {client.clientName}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Types
                </label>
                <select
                  multiple
                  name="verificationTypes"
                  value={formData.verificationTypes.map(type => type.id)}
                  onChange={handleVerificationTypeChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {verificationTypes.map(type => (
                    <option key={type._id} value={type._id}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign Users <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search users..."
                    onChange={(e) => {
                      const searchTerm = e.target.value.toLowerCase();
                      const filtered = users.filter(user => 
                        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm)
                      );
                      setFilteredUsers(filtered);
                      setShowUserSuggestions(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showUserSuggestions && filteredUsers.length > 0 && (
                    <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
                      {filteredUsers.map(user => (
                        <div
                          key={user._id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            handleUserSelect(user._id);
                            setShowUserSuggestions(false);
                          }}
                        >
                          {user.firstName} {user.lastName}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign Groups
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search groups..."
                    onChange={(e) => {
                      const searchTerm = e.target.value.toLowerCase();
                      const filtered = groups.filter(group => 
                        group.name.toLowerCase().includes(searchTerm)
                      );
                      setFilteredGroups(filtered);
                      setShowGroupSuggestions(true);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showGroupSuggestions && filteredGroups.length > 0 && (
                    <div className="absolute w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
                      {filteredGroups.map(group => (
                        <div
                          key={group._id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            handleGroupSelect(group._id);
                            setShowGroupSuggestions(false);
                          }}
                        >
                          {group.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned Users
                </label>
                <div className="flex flex-wrap gap-2">
                  {getSelectedUsers().map(user => (
                    <span 
                      key={user.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-500 text-white"
                    >
                      {user.name}
                      <button
                        onClick={() => removeUser(user.id)}
                        className="ml-2 focus:outline-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned Groups
                </label>
                <div className="flex flex-wrap gap-2">
                  {getSelectedGroups().map(group => (
                    <span 
                      key={group.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-500 text-white"
                    >
                      {group.name}
                      <button
                        onClick={() => removeGroup(group.id)}
                        className="ml-2 focus:outline-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deadline <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Loading...' : task ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm;